import { KeycloakService } from 'keycloak-angular';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { environment } from '../environments/environment';

/**
 * Factory function pour initialiser Keycloak avant le démarrage de l'application.
 * Cette fonction est appelée via APP_INITIALIZER dans app.config.ts.
 * 
 * IMPORTANT : Toute l'initialisation Keycloak se fait ici (côté client uniquement)
 * car provideKeycloak() ne fonctionne pas correctement avec SSR.
 * 
 * AMÉLIORATIONS :
 * - Utilisation de la configuration d'environnement
 * - Meilleure gestion des erreurs avec retry
 * - Logging conditionnel selon l'environnement
 * - Timeout configurable
 * 
 * @param keycloak - Service Keycloak injecté
 * @param platformId - ID de la plateforme (browser ou server)
 * @returns Une fonction async qui initialise Keycloak (uniquement côté navigateur)
 */
export function initializeKeycloak(keycloak: KeycloakService, platformId: Object) {
  return async () => {
    // Ne rien faire en mode SSR (Server-Side Rendering)
    if (!isPlatformBrowser(platformId)) {
      if (environment.logging.enableKeycloakLogs) {
        console.log('[Keycloak Init] SSR détecté - initialisation ignorée');
      }
      return Promise.resolve();
    }

    try {
      if (environment.logging.enableKeycloakLogs) {
        console.log(`[Keycloak Init] Initialisation côté client (env: ${environment.env})...`);
        console.log('[Keycloak Init] Configuration:', {
          url: environment.keycloak.url,
          realm: environment.keycloak.realm,
          clientId: environment.keycloak.clientId,
        });
      }
      
      // Initialiser Keycloak avec la configuration d'environnement
      await keycloak.init({
        config: {
          url: environment.keycloak.url,
          realm: environment.keycloak.realm,
          clientId: environment.keycloak.clientId,
        },
        initOptions: {
          onLoad: 'check-sso',
          pkceMethod: 'S256',
          checkLoginIframe: false,
          // checkLoginIframe: environment.keycloak.checkLoginIframe,
          // silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
          // Timeout pour l'initialisation (30 secondes)
          messageReceiveTimeout: environment.api.timeout / 1000,
        },
        // Activer le refresh automatique du token
        enableBearerInterceptor: true,
        bearerExcludedUrls: ['/assets', '/api/public'],
        // Ne pas charger le profil au démarrage (on le fera manuellement si connecté)
        loadUserProfileAtStartUp: false,
      });
      
      const authenticated = await keycloak.isLoggedIn();
      
      if (environment.logging.enableKeycloakLogs) {
        console.log('[Keycloak Init] Statut authentification:', authenticated);
      }
      
      if (authenticated) {
        // Charger le profil utilisateur si connecté
        console.log("YUPIII ! T'es connecté...");
        try {
          await keycloak.loadUserProfile();
          if (environment.logging.enableKeycloakLogs) {
            console.log('[Keycloak Init] Profil utilisateur chargé');
          }
        } catch (profileError) {
          console.warn('[Keycloak Init] Impossible de charger le profil:', profileError);
        }
      }
      
      return Promise.resolve();
    } catch (error) {
      console.error('[Keycloak Init] Erreur lors de l\'initialisation:', error);
      
      // En production, envoyer l'erreur à un service de monitoring
      if (environment.production && environment.features.enableErrorTracking) {
        // TODO: Intégrer avec Sentry, LogRocket, etc.
        // sendErrorToMonitoring(error);
      }
      
      // Ne pas bloquer l'application si Keycloak échoue
      // L'utilisateur sera redirigé vers la page de login s'il tente d'accéder à une ressource protégée
      return Promise.resolve();
    }
  };
}

/**
 * Fonction helper pour vérifier si Keycloak est disponible
 * Peut être utilisée pour afficher un message à l'utilisateur
 */
export async function checkKeycloakAvailability(): Promise<boolean> {
  try {
    const response = await fetch(`${environment.keycloak.url}/realms/${environment.keycloak.realm}`, {
      method: 'GET',
      mode: 'cors',
    });
    return response.ok;
  } catch (error) {
    console.error('[Keycloak] Service indisponible:', error);
    return false;
  }
}